import { useEffect, useRef } from 'react';

export interface UseInfiniteScrollSentinelOptions {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  // Distance before the sentinel enters the viewport at which the next page starts loading.
  rootMargin?: string;
}

// useInfiniteScrollSentinel loads the next page whenever the returned sentinel element scrolls
// near the viewport. Attach the ref to an empty element placed after the list.
export function useInfiniteScrollSentinel<Element extends HTMLElement = HTMLDivElement>({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = '240px 0px',
}: UseInfiniteScrollSentinelOptions) {
  const sentinelRef = useRef<Element>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const canObserve = sentinel && hasNextPage && !isFetchingNextPage;
    if (!canObserve) return;

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) void fetchNextPage(); },
      { rootMargin }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, rootMargin]);

  return sentinelRef;
}
